import type { GridProps } from '@risk-smart/themed-cloudscape-components/grid';

/**
 * Column spans for a drill-down dashboard's `Grid`.
 *
 * Reflow rather than scroll or squeeze. Cloudscape measures these breakpoints on the
 * grid's own container, so a dashboard responds to its content area rather than to the
 * window — the nav rail being open or closed moves it.
 *
 *   `m` (1120px) and up   all columns across
 *   `xs` (688px) and up   two across, remainder wrapping beneath
 *   below `xs`            stacked
 *
 * **Why `m` and not `xs`.** The cascade reads left-to-right, so it is tempting to hold
 * every column on screen for as long as possible. Measured, that trade does not pay: at
 * an `xs` container four columns leave ~115px of card content each, which is narrower
 * than the 80px minimum on a rating badge plus its friendly id, and narrow enough that
 * a category title is squeezed until it breaks mid-word ("High-Level Standar/ds",
 * "Chapter/s"). At `m` the same four columns get ~225px, which holds a two-line title
 * beside its create action, a card title breaking only at spaces, and an id and badge
 * on one row. Two legible columns beat four illegible ones.
 *
 * Shared by the Compliance (4), Risk (3) and Internal Audit (3) dashboards so the three
 * clones cannot drift apart again — before COI-739 they were `xs`/`m`/`xs` with three
 * comments each claiming a different breakpoint.
 *
 * @param columns how many drill-down columns the dashboard has. Cloudscape's grid is
 *   twelfths, so only counts that divide 12 evenly can be laid out.
 */
export const drillDownGridDefinition = (
  columns: 3 | 4
): GridProps.ElementDefinition[] =>
  Array.from({ length: columns }, () => ({
    colspan: { default: 12, xs: 6, m: 12 / columns },
  }));
